import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  Row,
  Col,
  Spinner,
  Container,
  Modal,
  Form
} from "react-bootstrap";
import axios from "axios";
import { useAuth } from "../../Context/AuthContext";
import { FaExclamationTriangle, FaUserCircle } from "react-icons/fa";
import { ImageOff } from "lucide-react";
import "./SuperAdminDashboard.css";
import { isExperimental } from "../../utils/isExperimental";

const SuperAdminDashboard = () => {
  const [complaints, setComplaints] = useState([]);
  const [filteredComplaints, setFilteredComplaints] = useState([]);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [actionType, setActionType] = useState("");
  const [note, setNote] = useState("");
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [expandedComplaint, setExpandedComplaint] = useState(null);
  const [infoComplaint, setInfoComplaint] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Popup full-card modal
  const [fullViewComplaint, setFullViewComplaint] = useState(null);

  // Image modal
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState(null);

  const baseUrl = process.env.REACT_APP_COMPLAINTS_APP_BE_URL;

  // Fetch complaints
  useEffect(() => {
    const fetchFlaggedComplaints = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const { data } = await axios.post(
          `${baseUrl}/admin-api/superadmin/flagged-complaints`,
          { email: user?.email },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setComplaints(data);
        setFilteredComplaints(data);
      } catch (err) {
        console.error("Error fetching flagged complaints:", err);
      } finally {
        setLoading(false);
      }
    };
    if (user?.email) fetchFlaggedComplaints();
  }, [user, baseUrl]);

  const [filters, setFilters] = useState({ category: "All", status: "All" });

  const handleAction = async (complaintId) => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${baseUrl}/admin-api/superadmin/complaints/${complaintId}/action`,
        { action: actionType, note, email: user?.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setComplaints((prev) =>
        prev.filter((c) => c.complaint_id !== complaintId)
      );

      setSelectedComplaint(null);
      setNote("");
      setSuccessMessage(
  actionType === "valid"
    ? "Complaint marked as Valid"
    : "User warned successfully"
);

setTimeout(() => {
  setSuccessMessage("");
}, 2000);
      if (isExperimental) alert("📧 Demo: Action email simulated.");
    } catch (err) {
      console.error("Error performing action:", err);
      alert("Failed to perform action");
    }
  };

  const emptyState = (
    <div className="text-center mt-5">
      <FaExclamationTriangle size={60} className="text-muted mb-3" />
      <h5 className="text-muted">No flagged Requests available</h5>
      <p className="text-muted">Everything looks clean for now.</p>
    </div>
  );

  const uniqueCategories = [...new Set(complaints.map((c) => c.category))].filter(Boolean);
  const uniqueStatuses = [...new Set(complaints.map((c) => c.status))].filter(Boolean);

  useEffect(() => {
    let filtered = [...complaints];
    if (filters.category && filters.category !== "All") {
      filtered = filtered.filter((c) => c.category === filters.category);
    }
    if (filters.status && filters.status !== "All") {
      filtered = filtered.filter((c) => c.status === filters.status);
    }
    setFilteredComplaints(filtered);
  }, [filters, complaints]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Pending":
        return <span className="status-pill status-pending">Pending</span>;
      case "Ongoing":
        return <span className="status-pill status-ongoing">Ongoing</span>;
      case "Resolved":
        return <span className="status-pill status-resolved">Resolved</span>;
      case "Reopened":
        return <span className="status-pill status-reopened">🔄 Reopened</span>;
      default:
        return null;
    }
  };

  return (
    <div className="superadmin-container">
      <Container fluid>
        <h2 className=" text-center mb-4 page-title">
          Super Admin - Flagged Requests
        </h2>


        {/* Loading */}
        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" variant="light" />
            <p className="mt-3 text-muted">Loading flagged requests...</p>
          </div>
        ) : complaints.length === 0 ? (
          emptyState
        ) : (
          <>
            <div className="filter-container">
              <div className="filter-box-grid">
                <div className="filter-group">
                  <div className="filter-title"></div>
                  <Form.Select
                    className="filter-select-bootstrap"
                    value={filters.category}
                    onChange={(e) => handleFilterChange("category", e.target.value)}
                  >
                    <option value="All">All Categories</option>
                    {uniqueCategories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                
                <div className="filter-group">
                  <div className="filter-title"></div>
                  <Form.Select
                    className="filter-select-bootstrap"
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                  >
                    <option value="All">All Status</option>
                    {uniqueStatuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </Form.Select>
                </div>

              </div>
            </div>

            <div className="cards-container">
              <Row className="gx-3 gy-3">
                {filteredComplaints.map((complaint) => {
                const words = complaint.description.split(" ");
                const shortDesc = words.slice(0, 25).join(" ");

                return (
                  <Col
                    key={complaint.complaint_id}
                    xs={12}
                    md={6}
                    lg={4}
                    xl={4}
                   >
                      <Card className="flag-card h-100">
                        <div className="position-relative complaint-image-wrapper">
                          {complaint.image ? (
                            <img
                              src={complaint.image}
                              alt="Complaint"
                              className="complaint-image"
                              onClick={() => {
                                setModalImageSrc(complaint.image);
                                setShowImageModal(true);
                              }}
                            />
                          ) : (
                            <div className="no-image-placeholder">
                              <ImageOff size={30} strokeWidth={1.6} />
                              <span>No Image Available</span>
                            </div>
                          )}

                          <div
                            className="status-overlay position-absolute"
                            style={{ top: "10px", left: "10px", zIndex: 3 }}
                          >
                            {getStatusBadge(complaint.status)}
                          </div>
                        </div>

                        <div className="category-chip">{complaint.category}</div>

                        <Card.Body className="flag-body">
                          <div className="complaint-text-block">
                            <h5 className="card-title-custom">{complaint.title}</h5>

                        {/* Description with "View More" */}
                        <Card.Text className="description">
                          {words.length > 25
                            ? shortDesc + "..."
                            : complaint.description}

                          {words.length > 25 && (
                            <Button
                              variant="link"
                              className="read-more"
                              onClick={() => setFullViewComplaint(complaint)}
                            >
                              View More
                            </Button>
                          )}
                        </Card.Text>
                        {complaint.flagged?.reason && (
                          <div className="mb-2 d-flex align-items-center">
                            <span className="detail-label me-2">Reason:</span>
                            <span className="reason-badge">{complaint.flagged.reason}</span>
                          </div>
                        )}
</div>
                        {/* Details */}
                        <div className="details-section">
                         {/*
                          <div className="detail-row">
                            <span className="detail-label text-dark">User:</span>
                            <span className="">
                              <FaUserCircle size={16} className="user-icon me-1" />
                              {complaint.user_id}
                            </span>
                          </div>

                          <div className="detail-row">
                            <span className="detail-label text-dark">Flagged By:</span>
                            <span className="">
                              {complaint.flagged.flaggedBy}
                            </span>
                          </div>

                          <div className="detail-row">
                            <span className="detail-label text-dark">Reason:</span>
                            <span className="reason-badge">
                              {complaint.flagged.reason}
                            </span>
                          </div>
*/}
                          {/* {complaint.flagged.note && (
                          <div className="detail-row">
                            <span className="detail-label text-dark">Note:</span>
                            <span className="">
                              {complaint.flagged.note}
                            </span>
                          </div>
                        )} */}


                          {complaint.flagged.note && (
                            <div className="detail-row">
                              <span className="detail-label text-dark">Note:</span>

                              <span className="note-preview">
                                {complaint.flagged.note.length > 120
                                  ? complaint.flagged.note.slice(0, 120) + "..."
                                  : complaint.flagged.note}

                                {complaint.flagged.note.length > 120 && (
                                  <Button
                                    variant="link"
                                    className="read-more"
                                    onClick={() => setFullViewComplaint(complaint)}
                                  >
                                    View More
                                  </Button>
                                )}
                              </span>
                            </div>
                          )}


                        </div>

                        <hr className="separator" />
                        <div className="action-section">
                        <div className="text-center mt-3">
  <Button
  className="super-btn super-info"
  onClick={() => setInfoComplaint(complaint)}
>
  Show More Info
</Button>
</div>
</div>

                        {/* Buttons */}
                        <div className="btn-row">
                          <Button
                            className="super-btn super-valid"
                            onClick={() => {
                              setSelectedComplaint(complaint);
                              setActionType("valid");
                            }}
                          >
                            ✔ Mark Valid
                          </Button>

                          <Button
                            className="super-btn super-warn"
                            onClick={() => {
                              setSelectedComplaint(complaint);
                              setActionType("warn");
                            }}
                          >
                            ⚠ Warn User
                          </Button>
                        </div>


                        <hr className="separator bottom" />
{/*
                        <div className="card-id-footer">
                          ID: {complaint.complaint_id}
                        </div>
   */}
                        </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>
          </>
        )}

        {/* ACTION MODAL */}
        {/* ACTION MODAL */}
        {selectedComplaint && (
          <Modal
            show={!!selectedComplaint}
            onHide={() => setSelectedComplaint(null)}
            centered
            className="white-modal"
          >
            <Modal.Header closeButton>
              <Modal.Title className="modal-header-title">
                Confirm Action
              </Modal.Title>
            </Modal.Header>

            <Modal.Body className="confirm-modal-body">
              <p className="confirm-text">
                Are you sure you want to{" "}
                <b
                  className={
                    actionType === "valid"
                      ? "highlight-valid"
                      : "highlight-warn"
                  }
                >
                  {actionType === "valid" ? "mark as Valid" : "Warn User"}
                </b>{" "}
                for this complaint?
              </p>

              {/* Title */}
              <div className="modal-section-title">Title</div>
              <div className="modal-description-box">
                <h5>{selectedComplaint.title}</h5>
              </div>

              {/* Description */}
              <div className="modal-section-title">Description</div>
              <div className="modal-description-box">
                {selectedComplaint.description}
              </div>

              {/* Note Input */}
              <div className="modal-section-title">Note (Optional)</div>
              <Form.Control
                as="textarea"
                rows={3}
                className="form-control note-textarea-bootstrap"
                placeholder="Add an internal note..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />

            </Modal.Body>

            <Modal.Footer className="justify-content-end gap-2">
              <Button
                variant="outline-secondary"
                className="btnsuperadmin"
                onClick={() => setSelectedComplaint(null)}
              >
                Cancel
              </Button>

              <Button
                variant={actionType === "valid" ? "success" : "warning"}
                onClick={() => handleAction(selectedComplaint.complaint_id)}
              >
                Confirm
              </Button>
            </Modal.Footer>


          </Modal>
        )}


        {/* FULL VIEW POPUP */}
        <Modal
          show={!!fullViewComplaint}
          onHide={() => setFullViewComplaint(null)}
          centered
          className="superadmin-detail-modal detail-modal no-scrollbar"
        >
          <Modal.Body>


            <button
              className="modal-close-x"
              onClick={() => setFullViewComplaint(null)}
              aria-label="Close"
            >
              ×
            </button>

            <h2 className="detail-title">Complaint Details</h2>

            <div className="detail-row">
              <div className="detail-label">Title:</div>
              <div className="detail-value">{fullViewComplaint?.title}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Description:</div>
              <div className="detail-value">{fullViewComplaint?.description}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">User:</div>
              <div className="detail-value">{fullViewComplaint?.user_id}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Category:</div>
              <div className="detail-value">{fullViewComplaint?.category}</div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Flagged By:</div>
              <div className="detail-value">{fullViewComplaint?.flagged?.flaggedBy}</div>
            </div>

            <div className="detail-row text-white">
              <div className="detail-label">Reason:</div>
              <div className="detail-value reason-box">
                {fullViewComplaint?.flagged?.reason}
              </div>
            </div>

            {fullViewComplaint?.flagged?.note && (
              <div className="detail-row">
                <div className="detail-label">Note:</div>
                <div className="detail-value">{fullViewComplaint?.flagged?.note}</div>
              </div>
            )}

            <div className="d-flex justify-content-center">
              <button
                className="detail-close-btn"
                onClick={() => setFullViewComplaint(null)}
              >
                Close
              </button>
            </div>

          </Modal.Body>
        </Modal>
{/* SHOW MORE INFO MODAL */}
<Modal
  show={!!infoComplaint}
  onHide={() => setInfoComplaint(null)}
  centered
    dialogClassName="complaint-info-modal"
>
  <Modal.Header closeButton>
    <Modal.Title>Complaint Information</Modal.Title>
  </Modal.Header>

  <Modal.Body>
  {console.log(infoComplaint)}
    <div className="d-flex align-items-center mb-3">
  <strong className="info-label">User:</strong>
  <span>{infoComplaint?.user_id}</span>
</div>

<div className="d-flex align-items-center">
  <strong className="info-label">Flagged By:</strong>
  <span>{infoComplaint?.flagged?.flaggedBy}</span>
</div>
<hr />

<h5 className="complaint-timeline-title">Complaint Timeline</h5>

<div className="timeline-item">
  <div className="timeline-dot submitted"></div>
  <div>
    <strong>Complaint Submitted</strong>
    <div className="text-muted">
      {new Date(infoComplaint?.timestamp).toLocaleString()}
    </div>
  </div>
</div>

<div className="timeline-item">
  <div className="timeline-dot flagged"></div>
  <div>
    <strong>Complaint Flagged</strong>
    <div className="text-muted">
      {new Date(infoComplaint?.flagged?.flaggedAt).toLocaleString()}
    </div>
  </div>
</div>

<div className="timeline-item">
  <div className="timeline-dot status"></div>
  <div>
    <strong>Current Status</strong>
    <div className="text-muted">
     <span className="status-badge resolved">
    {infoComplaint?.status}
</span>
    </div>
  </div>
</div>

  </Modal.Body>
</Modal>
{successMessage && (
  <div className="success-popup">
    <div className="success-circle">✓</div>
    <h4>Success</h4>
    <p>{successMessage}</p>
  </div>
)}

      {/* IMAGE MODAL */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        centered
        size="lg"
        contentClassName="bg-dark border-0"
        style={{ zIndex: 9999 }}
        backdropClassName="custom-image-backdrop"
      >
        <Modal.Body
          className="p-0 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: "rgba(0,0,0,0.95)" }}
        >
          <img
            src={modalImageSrc}
            alt="Full preview"
            style={{
              width: "auto",
              maxWidth: "80%",
              height: "auto",
              maxHeight: "65vh",
              borderRadius: "10px",
              objectFit: "contain",
              boxShadow: "0 0 15px rgba(0,0,0,0.5)",
            }}
          />
          <button
            onClick={() => setShowImageModal(false)}
            style={{
              position: "absolute",
              top: "15px",
              right: "20px",
              background: "rgba(255,255,255,0.2)",
              color: "white",
              border: "none",
              borderRadius: "50%",
              width: "35px",
              height: "35px",
              fontSize: "1.5rem",
              cursor: "pointer",
              zIndex: 10000,
            }}
          >
            ×
          </button>
        </Modal.Body>
      </Modal>

      </Container>
    </div>
  );
};

export default SuperAdminDashboard;